import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Folder } from '#app/components/icons/solid'
import { useCallback, useMemo, useState } from 'react'
import {
	Collection,
	DropIndicator,
	type Key,
	type Selection,
	useDragAndDrop,
} from 'react-aria-components'
import { Tree, TreeItem, TreeContent } from '#app/components/base/tree/tree.tsx'
import { Button } from '#app/components/shared/buttons/button.tsx'
import { trpc } from '#app/lib/trpc-tanstack.tsx'
import { ModalOverlay, Modal, Dialog } from './modal'

interface EditColorPalettesModalProps {
	palettes: Array<{ id: string; name: string; colors: string[]; order: number }>
	onOpenChange: (isOpen: boolean) => void
}

type LocalPalette = {
	id: string
	name: string
	colors: string[]
	order: number
}

type TreeNodeData = {
	id: string
	name: string
	type: 'palette' | 'color'
	paletteId: string
	hexValue?: string
	children: TreeNodeData[]
}

export function EditColorPalettesModal({
	palettes,
	onOpenChange,
}: EditColorPalettesModalProps) {
	const queryClient = useQueryClient()
	const [localPalettes, setLocalPalettes] = useState<LocalPalette[]>(() =>
		palettes.map((p) => ({ ...p, colors: [...p.colors] })),
	)
	const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set())
	const [isSelectionMode, setIsSelectionMode] = useState(false)
	const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(
		() => new Set(palettes.map((p) => `palette-${p.id}`)),
	)

	const bulkUpdateMutation = useMutation({
		...trpc.colorPalette.bulkUpdate.mutationOptions(),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: trpc.colorPalette.list.queryKey(),
			})
			onOpenChange(false)
		},
	})

	const treeData = useMemo<TreeNodeData[]>(() => {
		return localPalettes.map((palette) => ({
			id: `palette-${palette.id}`,
			name: palette.name,
			type: 'palette' as const,
			paletteId: palette.id,
			children: palette.colors.map((hex) => ({
				id: `color-${palette.id}-${hex}`,
				name: hex,
				type: 'color' as const,
				paletteId: palette.id,
				hexValue: hex,
				children: [],
			})),
		}))
	}, [localPalettes])

	const hasSelection =
		selectedKeys !== 'all' && (selectedKeys as Set<string>).size > 0

	const hasChanges = useMemo(() => {
		if (localPalettes.length !== palettes.length) return true
		return localPalettes.some((local, index) => {
			const original = palettes.find((p) => p.id === local.id)
			if (!original) return true
			// Check order change (compare by position in array)
			if (palettes[index]?.id !== local.id) return true
			if (local.colors.length !== original.colors.length) return true
			return local.colors.some((c, i) => c !== original.colors[i])
		})
	}, [localPalettes, palettes])

	const handleMove = useCallback(
		(e: {
			keys: Set<React.Key>
			target: { key: React.Key; dropPosition: 'before' | 'after' | 'on' }
		}) => {
			const draggedKeys = [...e.keys].map(String)
			const targetKey = String(e.target.key)
			const dropPosition = e.target.dropPosition

			setLocalPalettes((prev) => {
				const result = [...prev]

				// Parse what's being dragged
				const draggedPaletteIds: string[] = []
				const draggedColors: { paletteId: string; hex: string }[] = []

				for (const key of draggedKeys) {
					if (key.startsWith('palette-')) {
						draggedPaletteIds.push(key.replace('palette-', ''))
					} else if (key.startsWith('color-')) {
						const parts = key.split('-')
						const paletteId = parts[1]
						const hex = parts.slice(2).join('-')
						if (paletteId) draggedColors.push({ paletteId, hex })
					}
				}

				// Handle palette reordering
				if (
					draggedPaletteIds.length > 0 &&
					targetKey.startsWith('palette-') &&
					dropPosition !== 'on'
				) {
					const targetPaletteId = targetKey.replace('palette-', '')
					const targetIndex = result.findIndex((p) => p.id === targetPaletteId)
					if (targetIndex === -1) return prev

					// Remove dragged palettes
					const draggedPalettes = result.filter((p) =>
						draggedPaletteIds.includes(p.id),
					)
					const remaining = result.filter(
						(p) => !draggedPaletteIds.includes(p.id),
					)

					// Find new target index (might have shifted)
					const newTargetIndex = remaining.findIndex(
						(p) => p.id === targetPaletteId,
					)
					const insertIndex =
						dropPosition === 'after' ? newTargetIndex + 1 : newTargetIndex

					remaining.splice(insertIndex, 0, ...draggedPalettes)
					return remaining.map((p, i) => ({ ...p, order: i }))
				}

				// Handle color reordering or cross-palette moves
				if (draggedColors.length > 0) {
					// Drop ON palette = move colors to that palette
					if (targetKey.startsWith('palette-') && dropPosition === 'on') {
						const targetPaletteId = targetKey.replace('palette-', '')

						// Remove colors from source palettes
						for (const { paletteId, hex } of draggedColors) {
							const palette = result.find((p) => p.id === paletteId)
							if (palette) {
								palette.colors = palette.colors.filter((c) => c !== hex)
							}
						}

						// Add colors to target palette
						const targetPalette = result.find((p) => p.id === targetPaletteId)
						if (targetPalette) {
							const colorsToAdd = draggedColors.map((c) => c.hex)
							targetPalette.colors = [
								...targetPalette.colors,
								...colorsToAdd.filter((c) => !targetPalette.colors.includes(c)),
							]
						}

						return result
					}

					// Reorder colors within or between palettes (before/after on color)
					if (targetKey.startsWith('color-')) {
						const targetParts = targetKey.split('-')
						const targetPaletteId = targetParts[1]
						const targetHex = targetParts.slice(2).join('-')
						if (!targetPaletteId) return prev

						// Remove colors from source palettes
						const colorsToMove = draggedColors.map((c) => c.hex)
						for (const palette of result) {
							palette.colors = palette.colors.filter(
								(c) => !colorsToMove.includes(c),
							)
						}

						// Insert at target position
						const targetPalette = result.find((p) => p.id === targetPaletteId)
						if (targetPalette) {
							const targetIndex = targetPalette.colors.indexOf(targetHex)
							const insertIndex =
								targetIndex === -1
									? targetPalette.colors.length
									: dropPosition === 'after'
										? targetIndex + 1
										: targetIndex
							targetPalette.colors.splice(insertIndex, 0, ...colorsToMove)
						}

						return result
					}
				}

				return prev
			})
		},
		[],
	)

	const { dragAndDropHooks } = useDragAndDrop({
		getItems: (keys) => [...keys].map((key) => ({ 'text/plain': String(key) })),
		onReorder: handleMove,
		onMove: handleMove,
		renderDropIndicator: (target) => (
			<DropIndicator
				target={target}
				className={({ isDropTarget }) =>
					`-my-px h-0.5 w-full rounded-full outline-none ${isDropTarget ? 'bg-black dark:bg-white' : 'bg-transparent'}`
				}
			/>
		),
	})

	const handleDelete = () => {
		if (selectedKeys === 'all') return

		const selected = selectedKeys as Set<string>
		const palettesToDelete = new Set<string>()
		const colorsToRemove = new Map<string, Set<string>>()

		for (const key of selected) {
			if (key.startsWith('palette-')) {
				palettesToDelete.add(key.replace('palette-', ''))
			} else if (key.startsWith('color-')) {
				const parts = key.split('-')
				const paletteId = parts[1]
				const hex = parts.slice(2).join('-')
				if (paletteId) {
					if (!colorsToRemove.has(paletteId)) {
						colorsToRemove.set(paletteId, new Set())
					}
					colorsToRemove.get(paletteId)!.add(hex)
				}
			}
		}

		// Filter local state
		setLocalPalettes((prev) =>
			prev
				.filter((p) => !palettesToDelete.has(p.id))
				.map((p) => {
					const toRemove = colorsToRemove.get(p.id)
					if (!toRemove) return p
					return { ...p, colors: p.colors.filter((c) => !toRemove.has(c)) }
				}),
		)
		setSelectedKeys(new Set())
		setIsSelectionMode(false)
	}

	const handleSave = () => {
		bulkUpdateMutation.mutate(
			localPalettes.map((p, index) => ({
				id: p.id,
				colors: p.colors,
				order: index,
			})),
		)
	}

	const handleCancel = () => {
		onOpenChange(false)
	}

	return (
		<ModalOverlay isOpen onOpenChange={onOpenChange} isDismissable>
			<Modal className="w-full max-w-md">
				<Dialog className="p-0">
					<div className="bg-primary w-full overflow-hidden rounded-2xl shadow-xl">
						<div className="border-secondary flex items-center justify-between border-b px-4 py-3">
							<h2 className="text-primary text-sm font-semibold">
								Edit Palettes
							</h2>
							<div className="flex items-center gap-2">
								<Button color="secondary" size="sm" onClick={handleCancel}>
									Cancel
								</Button>
								<Button
									color="primary"
									size="sm"
									onClick={handleSave}
									isDisabled={!hasChanges}
									isLoading={bulkUpdateMutation.isPending}
								>
									Save
								</Button>
							</div>
						</div>

						{localPalettes.length > 0 && (
							<div className="border-secondary flex items-center justify-between border-b px-4 py-2">
								<Button
									color="link-color"
									size="sm"
									onClick={() => {
										if (isSelectionMode) {
											setSelectedKeys(new Set())
										}
										setIsSelectionMode(!isSelectionMode)
									}}
								>
									{isSelectionMode ? 'Cancel' : 'Select'}
								</Button>
								{isSelectionMode && hasSelection && (
									<Button
										color="secondary-destructive"
										size="sm"
										onClick={handleDelete}
									>
										Delete
									</Button>
								)}
							</div>
						)}

						<div className="max-h-80 overflow-y-auto p-2">
							{localPalettes.length === 0 ? (
								<p className="text-tertiary px-2 py-4 text-center text-sm">
									No palettes yet
								</p>
							) : (
								<Tree
									key={isSelectionMode ? 'selection' : 'drag-drop'}
									aria-label="Color palettes"
									selectionMode={isSelectionMode ? 'multiple' : 'none'}
									selectionBehavior="toggle"
									selectedKeys={selectedKeys}
									onSelectionChange={setSelectedKeys}
									expandedKeys={expandedKeys}
									onExpandedChange={setExpandedKeys}
									items={treeData}
									dragAndDropHooks={
										isSelectionMode ? undefined : dragAndDropHooks
									}
								>
									{function renderItem(node: TreeNodeData) {
										return (
											<TreeItem
												key={node.id}
												id={node.id}
												textValue={node.name}
											>
												<TreeContent>
													{node.type === 'palette' ? (
														<>
															<Folder
																className="text-fg-quaternary mr-2 size-4 shrink-0"
																data-slot="icon"
															/>
															<span className="truncate">{node.name}</span>
															<span className="text-quaternary ml-auto text-xs">
																{node.children.length}
															</span>
														</>
													) : (
														<>
															<span
																className="mr-2 size-4 shrink-0 rounded border"
																style={{ backgroundColor: node.hexValue }}
															/>
															<span className="font-mono text-xs">
																{node.hexValue}
															</span>
														</>
													)}
												</TreeContent>
												<Collection items={node.children}>
													{renderItem}
												</Collection>
											</TreeItem>
										)
									}}
								</Tree>
							)}
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	)
}
